import { OneTimePaymentKey, PlanKey, PlanTier, UserSubscription } from './payments.type';

export type StripeProvider = 'sunnyside' | 'openloop';

type GetPricesBaseParams = {
    provider: StripeProvider,
    type: 'recurring' | 'one-time',
    key: string,
    coupon?: string,
    variant?: string,
    currency?: string
}

export type GetPricesRecurringParams = GetPricesBaseParams & {
    type: 'recurring',
    key: PlanKey,
    tier: PlanTier
    trialDays?: number
}

export type GetPricesOneTimeParams = GetPricesBaseParams & {
    type: 'one-time',
    key: OneTimePaymentKey
}

export type GetPricesParams = GetPricesBaseParams & (GetPricesRecurringParams | GetPricesOneTimeParams)

export type PriceRecurringInterval = 'annual' | 'quarterly' | 'monthly';

export type ComposedRecurringPrices = {
    [key in PriceRecurringInterval]?: {
        price: number,
        priceWithDiscount: number,
        pricePerMonth: number,
        pricePerMonthWithDiscount: number,
        symbol: string,
        suffix: string,
        monthlySavings?: number,
        priceId: string
    }
}

export type ComposedOneTimePrice = {
    price: number,
    priceWithDiscount: number,
    priceId: string,
    symbol: string,
    suffix: string
}

export type PricesResponseUiConfig = {
    displayCurrencySelector: boolean
}

export type StripePrice = {
    id: string,
    object: 'price',
    active: boolean,
    created: number,
    metadata: Record<string, string>,
    nickname: string,
    product: string | { id: string },
    recurring?: {
        interval: 'day' | 'week' | 'month' | 'year',
        interval_count: number
    },
    currency: string,
    unit_amount: number
}

export type StripeCoupon = {
    id: string,
    object: 'coupon',
    amount_off: number | null,
    created: number,
    currency: string | null,
    duration: 'once' | 'repeating' | 'forever',
    name: string | null,
    percent_off: number | null,
    valid: boolean
}

type BasePricesResponse = {
    type: 'recurring' | 'one-time',
    uiConfig: PricesResponseUiConfig,
    coupons?: StripeCoupon[],
    currency: string,
    symbol: string,
    suffix: string
}

export type RecurringPricesResponse = BasePricesResponse & {
    type: 'recurring',
    prices: StripePrice[],
    composedPrices: ComposedRecurringPrices,
    trialDays?: number
}

export type OneTimePricesResponse = BasePricesResponse & {
    type: 'one-time',
    price: StripePrice,
    composedPrice: ComposedOneTimePrice
}

export type PricesResponse = RecurringPricesResponse | OneTimePricesResponse;

export type ReactivateResponse = {
    reactivated: false,
    redirectUrl?: string
} | {
    reactivated: true,
    redirectUrl?: string,
    userSubscription: UserSubscription
}

export type CreateCustomerPortalSessionResponse = {
    id: string,
    object: 'billing_portal.session',
    created: number,
    customer: string,
    livemode: boolean,
    return_url: string,
    url: string
}

export enum CheckoutSessionType {
    /**
     * Choose Plan
     * Direct link to choose plan with 15 day trial
     * app.cutbackcoach.com/l/cp/<hash_id>
     */
    CHOOSE_PLAN = 'cp',
    /**
     * Extend Trial
     * Choose plan to extend trial URL
     * (extend trial language at top, selecting plan starts 15 day trial)
     * app.cutbackcoach.com/l/et/<hash_id>
     */
    EXTEND_TRIAL = 'et',
    /**
     * Immediate Upgrade
     * Direct link to upgrade page (choosing plan charges immediately, no trial):
     * app.cutbackcoach.com/l/iu/<hash_id>
     */
    IMMEDIATE_UPGRADE = 'iu',
    /**
     * Switch Trial
     * Direct link to plan switch page, for current trial user (continues trial but on new plan)
     * app.cutbackcoach.com/l/st/<hash_id>
     */
    SWITCH_TRIAL = 'st',
    /**
    * Switch Plan
    * Direct link to plan switch page, for active sub
    * (switches plan and charges user for the difference OR issues credit if downgrading)
    * app.cutbackcoach.com/l/sp/<hash_id>
    */
    SWITCH_PLAN = 'sp',
    /**
    * Cyber Monday
    * Annual plan selection page with a 10% discount applied for cyber monday
    * app.cutbackcoach.com/l/cm/<hash_id>
    */
    CYBER_MONDAY = 'cm',
    /**
    * 2022 Discount
    * Annual plan selection page with a 22% discount applied for 2022 discount
    * app.cutbackcoach.com/l/2022/<hash_id>
    */
    NEW_YEAR_2022 = '2022',
    /**
     * Phone to Credit Card Trial (growth test)
     * For new flow where user creates subscription before starting diagnostic
     */
    PTCC = 'ptcc',
    /**
    * Immediate Upgrade on higher price
    * Immediate upgrade with a 20% discount applied
    * app.cutbackcoach.com/l/iu99_20/<hash_id>
    */
    IU99_20 = 'iu99_20',
    /**
    * Upgrade to premium coaching
    */
    PREMIUM_COACHING = 'pc',
    /**
    * Choose plan on higher price
    * Choose plan with a 20% discount applied
    * app.cutbackcoach.com/l/cp99_20/<hash_id>
    */
    CP99_20 = 'cp99_20',
    /**
    * Choose plan on higher price showing annual only plan
    * Choose plan with a 20% discount applied
    * app.cutbackcoach.com/l/cp_20/<hash_id>
    */
    CP_20 = 'cp_20',
    /**
    * Switch plan on higher price showing annual only plan
    * (switches plan and charges user for the difference OR issues credit if downgrading)
    * app.cutbackcoach.com/l/sp99_y_20/<hash_id>
    */
    SP99_Y = 'sp99_y',
    /**
    * Switch plan on higher price showing annual only plan
    * Switch plan with a 20% discount applied
    * (switches plan and charges user for the difference OR issues credit if downgrading)
    * app.cutbackcoach.com/l/sp99_y_20/<hash_id>
    */
    SP99_Y_20 = 'sp99_y_20',
    /**
    * Immediate Upgrade on higher price with all plans displayed
    * Immediate upgrade with a 20% discount applied on annual only
    * app.cutbackcoach.com/l/iu99_20_all/<hash_id>
    */
    IU99_20_ALL = 'iu99_20_all',
    /**
    * Immediate Upgrade on higher price with only annual plan displayed
    * app.cutbackcoach.com/l/iu99_y/<hash_id>
    */
    IU99_Y = 'iu99_y',
    /**
    * Add payment methoud checkout type
    */
    ADD_PAYMENT = 'add_payment',
    /**
    * NO CC Checkout
    */
    NO_CC = 'no_cc',
    /**
     * Premium
     */
    PREMIUM = 'premium'
}

type BaseCreateCheckoutSessionPrice = {
    type: 'recurring' | 'one-time',
    priceId: string
}

export type CreateCheckoutSessionRecurringPrice = BaseCreateCheckoutSessionPrice & {
    type: 'recurring'
}

export type CreateCheckoutSessionOneTimePrice = BaseCreateCheckoutSessionPrice & {
    type: 'one-time'
}


export type CreateNoCcCheckoutPayload = {
    trialDays?: number,
    coupon?: string
}

export type CreateExtendTrialCheckoutPayload = {
    trialDays?: number,
    successUrl?: string,
    planKey: PlanKey
}

export type CreateAddPaymentMethodCheckoutPayload = {
    /**
     * Success and cancel urls will only be used if the subscription is unable to be updated (
     * canceled or incomplete_expired status). Otherwise the user will be redirected to the invoice
     * payment page (note this should be opened in a new tab; checkout response will return ahrefTarget of '_blank')
     */
    successUrl?: string,
    cancelUrl?: string,
    planKey: PlanKey
}

export type CreateImmediateUpgradeCheckoutPayload = {
    price: CreateCheckoutSessionRecurringPrice,
    successUrl?: string,
    preserveTrial?: boolean,
    trialDays?: number
}

export type CreateCheckoutPayload = {
    prices: {
        recurring?: CreateCheckoutSessionRecurringPrice[],
        oneTime?: CreateCheckoutSessionOneTimePrice[]
    },
    /**
     * This will create a new subscription with priceId but will be in an "incomplete" state. After
     * 23 hours of the subscription not being paid it will be placed in an "incomplete_expired" state.
     * This state is the equivalent of a "canceled" state in Stripe meaning the subscription can not be
     * "reactivated" and the user will need to start a new subscription.
     *
     * NOTE: Currently on used when there are oneTime prices and no recurring prices.
     */
    pendingSubscription?: {
        priceId: string,
        trialDays?: number,
        coupon?: string
    },
    successUrl?: string,
    cancelUrl?: string,
    trialDays?: number,
    /**
     * Currently only supporting one until we update the stripe api/library. So the first coupon will
     * be applied.
     */
    coupons?: string[],
    shippingAddressCollection?: boolean
}

/**
 * Used solely to map the old checkout logic to the new checkout logic
 */
export type TempCreateCheckoutMap = {
    [CheckoutSessionType.IMMEDIATE_UPGRADE]: CreateImmediateUpgradeCheckoutPayload,
    [CheckoutSessionType.EXTEND_TRIAL]: CreateExtendTrialCheckoutPayload,
    [CheckoutSessionType.ADD_PAYMENT]: CreateAddPaymentMethodCheckoutPayload,
    [CheckoutSessionType.NO_CC]: CreateNoCcCheckoutPayload,
    [CheckoutSessionType.PREMIUM_COACHING]: CreateImmediateUpgradeCheckoutPayload,
    [CheckoutSessionType.SWITCH_TRIAL]: CreateImmediateUpgradeCheckoutPayload,
    [CheckoutSessionType.SWITCH_PLAN]: CreateImmediateUpgradeCheckoutPayload,
    /* The rest are just normal checkout payloads */
    [CheckoutSessionType.CYBER_MONDAY]: CreateCheckoutPayload,
    [CheckoutSessionType.NEW_YEAR_2022]: CreateCheckoutPayload,
    [CheckoutSessionType.PTCC]: CreateCheckoutPayload,
    [CheckoutSessionType.IU99_20]: CreateCheckoutPayload,
    [CheckoutSessionType.SP99_Y]: CreateCheckoutPayload,
    [CheckoutSessionType.SP99_Y_20]: CreateCheckoutPayload,
    [CheckoutSessionType.IU99_20_ALL]: CreateCheckoutPayload,
    [CheckoutSessionType.IU99_Y]: CreateCheckoutPayload,
    [CheckoutSessionType.CP_20]: CreateCheckoutPayload,
    [CheckoutSessionType.CP99_20]: CreateCheckoutPayload,
    [CheckoutSessionType.PREMIUM]: CreateCheckoutPayload,
    [CheckoutSessionType.CHOOSE_PLAN]: CreateCheckoutPayload
}

export type CreateCheckoutResponse = {
    id?: string,
    url?: string,
    ahrefTarget?: '_blank'
}

export type TempCheckoutInfoSetPayload = {
    checkout_type?: string | string[] | CheckoutSessionType,
    price_group?: string | string[],
    coupons?: string | string[],
    trial_days?: number,
    tier?: string | string[] | 'PREMIUM_COACHING',
    price_variant?: string | string[],
    premium_coaching_price_group?: string | string[],
    currency?: string
}

export type TempCheckoutInfoLocalStorage = {
    checkout_type?: CheckoutSessionType,
    price_group?: string,
    coupons?: string,
    trial_days?: number,
    tier?: string | 'PREMIUM_COACHING',
    price_variant?: string,
    premium_coaching_price_group?: string,
    currency?: string
}
